import { useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useAppSelector } from 'features/app/hooks';
import {
  createAltKeyTracker,
  getIsAllowedDialPadValue,
  handleNumericKeyWithDelay,
  isNumberKey,
  normalizeKeyboardValue,
  shouldBlockDialPadInput,
} from 'features/dial-pad/helpers';
import type { DialPadValue } from 'features/dial-pad/interfaces';
import { isDialPadOpenedSelector } from 'features/dial-pad/store';

const DEBOUNCE_DELAY_MS = 50;

interface UseDialPadKeyboardProps {
  onKeyValue: (value: DialPadValue) => void;
}

export const useDialPadKeyboard = ({ onKeyValue }: UseDialPadKeyboardProps) => {
  const isDialPadOpened = useAppSelector(isDialPadOpenedSelector);
  const altKeyRef = useRef(false);

  // Set up global Alt key state tracking
  useEffect(() => {
    const cleanup = createAltKeyTracker(altKeyRef);
    return cleanup;
  }, []);

  // Memoized handler for numeric keys with Alt timing detection
  const handleNumericKeyWithDelayMemo = useCallback(
    (event: KeyboardEvent) => {
      handleNumericKeyWithDelay(
        event,
        altKeyRef,
        isDialPadOpened,
        onKeyValue,
        DEBOUNCE_DELAY_MS
      );
    },
    [onKeyValue, isDialPadOpened]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      // Check if this key press should be blocked (Alt key active, dial pad closed, etc.)
      if (
        shouldBlockDialPadInput(
          event,
          isDialPadOpened,
          getIsAllowedDialPadValue
        )
      ) {
        return;
      }

      // Handle numeric keys with timing delay to catch subsequent Alt key presses
      if (isNumberKey(event.key)) {
        handleNumericKeyWithDelayMemo(event);
        return;
      }

      // Handle non-numeric keys immediately
      const dialPadValue = normalizeKeyboardValue(event.key);
      onKeyValue(dialPadValue);
    },
    [onKeyValue, isDialPadOpened, handleNumericKeyWithDelayMemo]
  );

  useHotkeys('*', handleKeyPress, {
    enabled: isDialPadOpened,
  });
};
